import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CalendarDays, Loader2, RefreshCw, Users } from "lucide-react";
import { TaskService } from "./TaskService";
import { filterTasksForUser } from "../../utils/taskFilters";

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_BAR_HEIGHT = 24;
const YEAR_BAR_HEIGHT = 20;
const BAR_GAP = 3;
const MONTH_BAR_STEP = MONTH_BAR_HEIGHT + BAR_GAP;
const YEAR_BAR_STEP = YEAR_BAR_HEIGHT + BAR_GAP;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const roleLower = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const isOwnerOrAdmin = (me) => {
  const role = roleLower(me?.role);
  return role === "owner" || role === "admin";
};

const toDate = (raw) => {
  if (!raw) return null;
  if (typeof raw?.toDate === "function") return raw.toDate();
  if (raw instanceof Date) return raw;
  if (typeof raw === "string") {
    const next = new Date(raw);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  if (typeof raw === "number") {
    const next = new Date(raw);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatDay = (date) =>
  date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

const formatMonthYear = (date) =>
  date.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });

const getTaskDates = (task) => {
  const due = toDate(task?.dueAt);
  const created =
    toDate(task?.startAt) ||
    toDate(task?.createdAt) ||
    toDate(task?.updatedAt) ||
    due;

  if (!due && !created) return null;

  const end = startOfDay(due || created);
  let start = startOfDay(created || due);

  if (!created && due) {
    const estimatedDays = Math.max(1, Math.ceil(Number(task?.estimatedHours || 1) / 8));
    start = new Date(end.getTime() - (estimatedDays - 1) * DAY_MS);
  }

  if (start > end) start = new Date(end);

  return { start, end };
};

const getTaskTone = (task) => {
  const status = String(task?.status || "To Do");
  if (status === "Completed") return "from-emerald-500 to-emerald-400";
  if (status === "Cancelled") return "from-rose-400 to-rose-300";
  if (status === "On Hold") return "from-amber-500 to-amber-400";
  if (status === "In Progress") return "from-blue-600 to-sky-500";
  return "from-slate-700 to-slate-500";
};

const getAssignedIds = (task) => {
  const direct = Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds.filter(Boolean) : [];
  if (direct.length) return direct;
  const memberIds = Array.isArray(task?.memberIds) ? task.memberIds.filter(Boolean) : [];
  if (memberIds.length) return memberIds;
  return [];
};

const getTaskProgress = (task) => {
  const subtasks = Array.isArray(task?.subtasks)
    ? task.subtasks
    : Array.isArray(task?.checklist)
      ? task.checklist
      : Array.isArray(task?.checklistItems)
        ? task.checklistItems
        : [];

  const total = subtasks.length;
  const done = subtasks.filter((item) => item?.isDone === true || item?.done === true).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, pct };
};

const displayUser = (user) =>
  user?.fullName || user?.name || user?.displayName || user?.email || user?.id || "User";

const getUserPhoto = (user) =>
  user?.photoURL ||
  user?.photoUrl ||
  user?.profilePhotoUrl ||
  user?.profilePhotoURL ||
  user?.profileImageUrl ||
  user?.avatarUrl ||
  user?.avatarURL ||
  user?.photo?.url ||
  "";

const getInitials = (value) => {
  const text = String(value || "").trim();
  if (!text) return "U";
  const parts = text.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const diffMonthsInclusive = (start, end) =>
  (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;

export default function TaskGantt({
  companyId,
  me,
  usersMap = {},
  groupsMap = {},
  onEdit,
  onRefresh,
}) {
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [zoom, setZoom] = useState("month");
  const monthScrollRef = useRef(null);
  const yearScrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      setError("");
      try {
        const all = await TaskService.getCompanyTasks(companyId, { me });
        if (!cancelled) setRawTasks(Array.isArray(all) ? all : []);
      } catch (err) {
        if (!cancelled) {
          console.error("[TaskGantt] load failed:", err);
          setError(err?.message || "Failed to load tasks.");
          setRawTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, me]);

  const tasks = useMemo(() => filterTasksForUser(rawTasks, me), [rawTasks, me]);

  const timelineTasks = useMemo(() => {
    return tasks
      .map((task) => {
        const dates = getTaskDates(task);
        if (!dates) return null;
        return { ...task, ganttDates: dates, progress: getTaskProgress(task) };
      })
      .filter(Boolean)
      .sort((a, b) => a.ganttDates.start - b.ganttDates.start);
  }, [tasks]);

  const normalizedUsersMap = useMemo(() => {
    const map = {};
    Object.entries(usersMap || {}).forEach(([key, user]) => {
      if (!user) return;
      map[key] = user;
      if (user.uid) map[user.uid] = user;
      if (user.id) map[user.id] = user;
    });
    return map;
  }, [usersMap]);

  const dateRange = useMemo(() => {
    if (!timelineTasks.length) return null;
    const starts = timelineTasks.map((task) => task.ganttDates.start.getTime());
    const ends = timelineTasks.map((task) => task.ganttDates.end.getTime());
    const min = new Date(Math.min(...starts));
    const max = new Date(Math.max(...ends));
    const start = new Date(min.getTime() - 2 * DAY_MS);
    const end = new Date(max.getTime() + 2 * DAY_MS);
    const days = [];
    for (let cursor = new Date(start); cursor <= end; cursor = new Date(cursor.getTime() + DAY_MS)) {
      days.push(new Date(cursor));
    }
    return { start, end, days };
  }, [timelineTasks]);

  const rows = useMemo(() => {
    const map = new Map();

    const upsertRow = (id, label, icon = "user", user = null) => {
      if (!map.has(id)) {
        map.set(id, {
          id,
          label,
          icon,
          tasks: [],
          photoUrl: getUserPhoto(user),
          initials: getInitials(label),
        });
      } else if (user && !map.get(id).photoUrl) {
        const current = map.get(id);
        current.photoUrl = getUserPhoto(user);
      } else if (!map.get(id).initials) {
        map.get(id).initials = getInitials(label);
      }
      return map.get(id);
    };

    timelineTasks.forEach((task) => {
      const assignedIds = getAssignedIds(task);

      if (assignedIds.length) {
        assignedIds.forEach((id) => {
          const user = normalizedUsersMap[id];
          upsertRow(id, displayUser(user), "user", user).tasks.push(task);
        });
        return;
      }

      if (task?.groupId) {
        const group = groupsMap[task.groupId];
        upsertRow(`group:${task.groupId}`, group?.name || task?.groupName || "Group", "group").tasks.push(task);
        return;
      }

      upsertRow("unassigned", "Unassigned", "user").tasks.push(task);
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [timelineTasks, normalizedUsersMap, groupsMap]);

  const monthSegments = useMemo(() => {
    if (!dateRange?.days?.length) return [];
    const segments = [];
    let cursorMonth = dateRange.days[0].getMonth();
    let cursorYear = dateRange.days[0].getFullYear();
    let span = 0;

    dateRange.days.forEach((day, index) => {
      const sameBucket = day.getMonth() === cursorMonth && day.getFullYear() === cursorYear;
      if (sameBucket) {
        span += 1;
        return;
      }

      segments.push({
        key: `${cursorYear}-${cursorMonth}`,
        label: `${MONTH_LABELS[cursorMonth]} ${cursorYear}`,
        span,
      });

      cursorMonth = day.getMonth();
      cursorYear = day.getFullYear();
      span = 1;

      if (index === dateRange.days.length - 1) {
        segments.push({
          key: `${cursorYear}-${cursorMonth}`,
          label: `${MONTH_LABELS[cursorMonth]} ${cursorYear}`,
          span,
        });
      }
    });

    if (!segments.length || segments[segments.length - 1]?.key !== `${cursorYear}-${cursorMonth}`) {
      segments.push({
        key: `${cursorYear}-${cursorMonth}`,
        label: `${MONTH_LABELS[cursorMonth]} ${cursorYear}`,
        span,
      });
    }

    return segments;
  }, [dateRange]);

  const yearRange = useMemo(() => {
    if (!timelineTasks.length) return null;
    const starts = timelineTasks.map((task) => startOfMonth(task.ganttDates.start).getTime());
    const ends = timelineTasks.map((task) => startOfMonth(task.ganttDates.end).getTime());
    const start = new Date(Math.min(...starts));
    const end = new Date(Math.max(...ends));
    const months = [];
    for (let cursor = new Date(start); cursor <= end; cursor = addMonths(cursor, 1)) {
      months.push(new Date(cursor));
    }
    return { start, end, months };
  }, [timelineTasks]);

  useEffect(() => {
    const today = startOfDay(new Date());
    if (zoom === "month" && dateRange?.days?.length && monthScrollRef.current) {
      const todayIndex = dateRange.days.findIndex(
        (day) => day.getFullYear() === today.getFullYear() && day.getMonth() === today.getMonth() && day.getDate() === today.getDate()
      );
      if (todayIndex >= 0) {
        monthScrollRef.current.scrollLeft = Math.max(0, todayIndex * 42 - 260);
      }
    }

    if (zoom === "year" && yearRange?.months?.length && yearScrollRef.current) {
      const todayMonth = startOfMonth(today);
      const todayIndex = yearRange.months.findIndex(
        (month) => month.getFullYear() === todayMonth.getFullYear() && month.getMonth() === todayMonth.getMonth()
      );
      if (todayIndex >= 0) {
        yearScrollRef.current.scrollLeft = Math.max(0, todayIndex * 64 - 260);
      }
    }
  }, [zoom, dateRange, yearRange]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const all = await TaskService.getCompanyTasks(companyId, { me });
      setRawTasks(Array.isArray(all) ? all : []);
      if (typeof onRefresh === "function") await onRefresh();
    } catch (err) {
      console.error("[TaskGantt] refresh failed:", err);
      setError(err?.message || "Failed to refresh tasks.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700">
        <div className="flex items-center gap-3 text-sm font-medium">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Gantt view…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!dateRange || !rows.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <CalendarDays className="mx-auto h-8 w-8 text-slate-300" />
        <div className="mt-3 text-sm font-semibold text-slate-900">No scheduled tasks yet</div>
        <div className="mt-1 text-sm text-slate-500">
          Tasks with due dates will show here as timeline bars by teammate.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Gantt Timeline</div>
          <div className="text-xs text-slate-500">
            {zoom === "year"
              ? `${rows.length} lanes across ${yearRange?.months?.length || 0} months`
              : `${rows.length} lanes across ${dateRange.days.length} days`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            {[
              { id: "month", label: "Month" },
              { id: "year", label: "Year" },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setZoom(option.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  zoom === option.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {zoom === "year" && yearRange ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex min-w-0">
            <div className="w-[260px] shrink-0 border-r border-slate-200 bg-white">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Team / Task Lane
              </div>
              {rows.map((row) => {
                const rowHeight = Math.max(42, row.tasks.length * YEAR_BAR_STEP + 12);
                return (
                  <div
                    key={`${row.id}-year-label`}
                    className="border-b border-slate-200 bg-white px-4 py-3"
                    style={{ minHeight: `${rowHeight}px` }}
                  >
                    <div className="flex items-center gap-2">
                      {row.icon === "group" ? (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <Users className="h-4 w-4" />
                        </div>
                      ) : row.photoUrl ? (
                        <img
                          src={row.photoUrl}
                          alt={row.label}
                          className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                          {row.initials || getInitials(row.label)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{row.label}</div>
                        <div className="text-xs text-slate-500">{row.tasks.length} scheduled task{row.tasks.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={yearScrollRef} className="min-w-0 flex-1 overflow-x-auto">
              <div
                className="grid min-w-[720px]"
                style={{ gridTemplateColumns: `repeat(${yearRange.months.length}, minmax(64px, 1fr))` }}
              >
                {yearRange.months.map((month) => (
                  <div
                    key={month.toISOString()}
                    className="border-b border-slate-200 bg-slate-50 px-1 py-2 text-center text-[11px] font-medium text-slate-600"
                  >
                    <div>{MONTH_LABELS[month.getMonth()]}</div>
                    <div className="mt-0.5 text-slate-400">{month.getFullYear()}</div>
                  </div>
                ))}

                {rows.map((row) => {
                  const totalMonths = Math.max(1, yearRange.months.length);
                  const rowHeight = Math.max(42, row.tasks.length * YEAR_BAR_STEP + 12);
                  return (
                    <div key={`${row.id}-year-track`} className="relative border-b border-slate-200" style={{ minHeight: `${rowHeight}px`, gridColumn: `1 / span ${totalMonths}` }}>
                      <div
                        className="grid h-full"
                        style={{ gridTemplateColumns: `repeat(${totalMonths}, minmax(64px, 1fr))` }}
                      >
                        {yearRange.months.map((month) => (
                          <div
                            key={`${row.id}-${month.toISOString()}`}
                            className="border-r border-slate-100 bg-white"
                          />
                        ))}
                      </div>
                      <div className="pointer-events-none absolute inset-0 px-1 py-1.5">
                        {row.tasks.map((task, index) => {
                          const monthStart = startOfMonth(task.ganttDates.start);
                          const monthEnd = startOfMonth(task.ganttDates.end);
                          const startOffset = diffMonthsInclusive(yearRange.start, monthStart) - 1;
                          const spanMonths = Math.max(1, diffMonthsInclusive(monthStart, monthEnd));
                          const leftPct = (startOffset / totalMonths) * 100;
                          const widthPct = (spanMonths / totalMonths) * 100;
                          const topPx = 6 + index * YEAR_BAR_STEP;
                          return (
                            <button
                              key={task.id}
                              type="button"
                              onClick={() => onEdit?.(task)}
                              className={`pointer-events-auto absolute flex h-5 items-center overflow-hidden rounded-md bg-gradient-to-r ${getTaskTone(task)} px-2 text-left text-white shadow-sm transition hover:brightness-105`}
                              style={{
                                left: `${leftPct}%`,
                                width: `calc(${widthPct}% - 6px)`,
                                top: `${topPx}px`,
                              }}
                              title={`${task.title || "Untitled task"}${task.progress?.total ? ` • ${task.progress.done}/${task.progress.total} • ${task.progress.pct}%` : ""} • ${formatMonthYear(task.ganttDates.start)} to ${formatMonthYear(task.ganttDates.end)}`}
                            >
                              <span className="truncate text-[10px] font-semibold">{task.title || "Untitled task"}</span>
                              {task.progress?.total ? (
                                <span className="ml-1 shrink-0 rounded-full bg-white/15 px-1 py-0.5 text-[9px] font-semibold">
                                  {task.progress.done}/{task.progress.total}
                                </span>
                              ) : null}
                              {task.progress?.total ? (
                                <span
                                  className="pointer-events-none absolute bottom-0 left-0 h-[2px] bg-white/80"
                                  style={{ width: `${task.progress.pct}%` }}
                                />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex min-w-0">
            <div className="w-[260px] shrink-0 border-r border-slate-200 bg-white">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Team / Task Lane
              </div>
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-medium text-slate-500">
                Detail
              </div>
              {rows.map((row) => {
                const rowHeight = Math.max(48, row.tasks.length * MONTH_BAR_STEP + 12);
                return (
                  <div
                    key={`${row.id}-label`}
                    className="border-b border-slate-200 bg-white px-4 py-3"
                    style={{ minHeight: `${rowHeight}px` }}
                  >
                    <div className="flex items-center gap-2">
                      {row.icon === "group" ? (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <Users className="h-4 w-4" />
                        </div>
                      ) : row.photoUrl ? (
                        <img
                          src={row.photoUrl}
                          alt={row.label}
                          className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
                          {row.initials || getInitials(row.label)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{row.label}</div>
                        <div className="text-xs text-slate-500">{row.tasks.length} scheduled task{row.tasks.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={monthScrollRef} className="min-w-0 flex-1 overflow-x-auto">
              <div
                className="grid min-w-[860px]"
                style={{ gridTemplateColumns: `repeat(${dateRange.days.length}, minmax(42px, 1fr))` }}
              >
                {monthSegments.map((segment) => (
                  <div
                    key={segment.key}
                    className="border-b border-r border-slate-200 bg-slate-50 px-2 py-1.5 text-center text-[11px] font-semibold text-slate-600"
                    style={{ gridColumn: `span ${segment.span}` }}
                  >
                    {segment.label}
                  </div>
                ))}

                {dateRange.days.map((day) => (
                  <div
                    key={day.toISOString()}
                    className="border-b border-slate-200 bg-slate-50 px-1 py-1.5 text-center text-[10px] font-medium text-slate-500"
                  >
                    <div>{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className="mt-0.5 text-[11px] text-slate-700">{day.getDate()}</div>
                  </div>
                ))}

                {rows.map((row) => {
                  const totalDays = Math.max(1, dateRange.days.length);
                  const rowHeight = Math.max(48, row.tasks.length * MONTH_BAR_STEP + 12);
                  return (
                    <div key={`${row.id}-track`} className="relative border-b border-slate-200" style={{ minHeight: `${rowHeight}px`, gridColumn: `1 / span ${totalDays}` }}>
                      <div
                        className="grid h-full"
                        style={{ gridTemplateColumns: `repeat(${totalDays}, minmax(42px, 1fr))` }}
                      >
                        {dateRange.days.map((day) => (
                          <div
                            key={`${row.id}-${day.toISOString()}`}
                            className={`border-r border-slate-100 ${day.getDay() === 0 || day.getDay() === 6 ? "bg-slate-50/70" : "bg-white"}`}
                          />
                        ))}
                      </div>

                      <div className="pointer-events-none absolute inset-0 px-1.5 py-1.5">
                        {row.tasks.map((task, index) => {
                          const startOffset = Math.round((task.ganttDates.start - dateRange.start) / DAY_MS);
                          const spanDays = Math.max(
                            1,
                            Math.round((task.ganttDates.end - task.ganttDates.start) / DAY_MS) + 1
                          );
                          const leftPct = (startOffset / totalDays) * 100;
                          const widthPct = (spanDays / totalDays) * 100;
                          const topPx = 6 + index * MONTH_BAR_STEP;
                          return (
                            <button
                              key={task.id}
                              type="button"
                              onClick={() => onEdit?.(task)}
                              className={`pointer-events-auto absolute flex h-6 items-center gap-2 overflow-hidden rounded-md bg-gradient-to-r ${getTaskTone(task)} px-2 text-left text-white shadow-sm transition hover:brightness-105`}
                              style={{
                                left: `${leftPct}%`,
                                width: `calc(${widthPct}% - 6px)`,
                                top: `${topPx}px`,
                              }}
                              title={`${task.title || "Untitled task"}${task.progress?.total ? ` • ${task.progress.done}/${task.progress.total} • ${task.progress.pct}%` : ""} • ${formatDay(task.ganttDates.start)} to ${formatDay(task.ganttDates.end)}`}
                            >
                              <span className="truncate text-[11px] font-semibold">
                                {task.title || "Untitled task"}
                              </span>
                              {task.progress?.total ? (
                                <span className="shrink-0 rounded-full bg-white/15 px-1.5 py-0.5 text-[9px] font-semibold">
                                  {task.progress.done}/{task.progress.total}
                                </span>
                              ) : null}
                              {isOwnerOrAdmin(me) ? (
                                <span className="ml-auto shrink-0 rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-medium">
                                  {task.status || "To Do"}
                                </span>
                              ) : null}
                              {task.progress?.total ? (
                                <span
                                  className="pointer-events-none absolute bottom-0 left-0 h-[2px] bg-white/80"
                                  style={{ width: `${task.progress.pct}%` }}
                                />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
